/* A PARTNER COMPANY'S OWN SAMPLE TRACK — Jay, 2026-10-17, mail "Sample Tracker — DAC VN login access and
   separate track (@dacpole.vn)"; Max, 2026-10-18: "có làm nhé".

   THE SAMPLE TRACKER'S OWN SCREEN, LESS THE PROJECT SKU — Max, 2026-10-18: "làm cho nó giống cái giao diện
   sample tracker của helinox nha. bê y chang logic và giao diện chỉ có cái là ko có project SKU thui. Mình
   làm cho Jay biết là nó trông như thế nào rùi sẽ soạn mail nói anh ấy gửi lại UI sau". So this is the All
   Samples screen of 15_SampleTracker.jsx — the two tabs, the two rows of cards, the filter bar, the Base ID
   table that folds open into its rounds, the round form, the Base ID form, the Excel export — built from the
   same class names, so it LOOKS the same, and from the same functions (exported from that file: roundStatus,
   groupSamplesOf, computeSampleStats, validateForm, the sort getters…), so it COUNTS the same.

   What is not here, and why:
     - the project SKU: the column, the Linked Project SKU block, the SKU's material list, and "Create
       Sample" picking a SKU — a partner's sample is created by typing its style, the way HEVN's trial
       sample is;
     - Target Price and Actual Cost (the approved design of 2026-10-18 took the price fields out);
     - the SMP / TSMP pills (one series here), the trial-sample button, and the guide.
   "HEVN fills" reads as the partner's own name, because on this track the partner is the one filling it.
   Jay is to send back the form he actually wants; until then this is a picture of what is possible.

   ONE SCREEN, TWO READERS. A partner's account sees its own company's samples and nothing else — the
   server decides that, from the email domain or the account's flag, and this screen never asks for anybody
   else's. On HEVN's side only the Admin role holds the grant (Max, 2026-10-18). */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Copy, Trash2, Plus, Download, Eye } from 'lucide-react';
import Header from './Header';
import TabIcon from './TabIcon';
import useAuth from './useAuth';
import useEscapeClose from './useEscapeClose';
import useDiscardGuard from './useDiscardGuard';
import ProjectPhotos from './ProjectPhotos';
import TourGuide from './TourGuide';
import { ManualButton, MANUAL_STEP } from './ModuleHelp';
import { BACKEND_URL } from './backendConfig';
import { fmtDateTimeVN } from './timeZone';
import i18n from './i18n';
import {
  SortTh, Field, Kpi, MultiFilter, IconButton, sortRows, rowPassesColumnFilters, distinctColumnValues,
  roundMatchesStatus, sampleMatchesRoundStatus, roundStyleLabel, roundStatus, outcomePillClass, productStatusPillClass, SHORT_OUTCOME,
  dDayClass, dDayLabel, fmt, num, fmtDays, calcPreview, validateForm, findDuplicateRound, nextRoundLabel,
  groupSamplesOf, computeSampleStats, SAMPLE_SORT_GETTERS, ROUND_FILTER_GETTERS, MULTI_FILTER_NONE, emptyForm,
  isInternalTrial, reasonOwnerClass, OUTCOME_DESCRIPTIONS, OUTCOME_DESC_STYLE, ROUND_OPTIONS, OUTCOME_OPTIONS,
  REWORK_REASON_OPTIONS, REASON_OWNER_MAP, CATEGORY_FALLBACK, SEASON_FALLBACK,
} from './15_SampleTracker';
import './ProjectPhotos.css';
import './SampleTracker.css';
import './PartnerSamples.css';
import { valueLabel } from './valueLabel';

const API = `${BACKEND_URL}/api/partner-samples`;
const PHOTO_API = '/api/partner-samples/photos';
const PHOTO_SCOPE = 'psample';

const sampleTabs = [
  { id: 'all', label: 'st.tabAll' },
  { id: 'product', label: 'st.tabSummary' },
];
const FILTERS_DEFAULT = { sampleStatus: [], roundStatus: [], developer: [], designer: [], location: [], org: [], search: '' };
const NO_COLUMN_FILTERS = {};

/* The Sample Tracker's grid less the Project SKU ID column (110 px), and since 2026-10-19 less the three
   columns of the HINC Design Review card Jay took off the form (Designer, Round Due, Received) — nothing
   could fill them. The first EIGHT are the frozen block now (through Developer): PartnerSamples.css stops
   the ninth from sticking. The table's width is the exact sum. */
const SAMPLE_COL_WIDTHS = [
  32, 50, 180, 320, 90, 90, 100, 110,
  120, 130, 115, 130, 115, 80, 85, 145, 90, 440, 130,
];
const COLS = SAMPLE_COL_WIDTHS.length;

/* The Excel file the Sample Tracker writes, less the Project SKU ID column and the three HINC columns —
   so Status is the 15th. */
const EXPORT_WIDTHS = [7, 14, 34, 10, 10, 11, 12, 13, 16, 13, 16, 13, 8, 9, 13, 9, 13, 72];

async function call(path, method = 'GET', body) {
  const r = await fetch(`${API}${path}`, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || i18n.t('xPartner.requestFailed', { status: r.status }));
  return j;
}

/* ---------------------------------------------------------------------------------------------------- */
/* THE ROUND FORM — the Sample Tracker's RoundModal, section for section, less the Linked Project SKU block,
   the two price fields and the SKU's material list. `creating` is its trial-sample path: the first round of
   a sample whose style is typed, the Base ID given from that name. */
function PartnerRoundModal({ mode, advancingFrom, form, lists, rounds, orgName, orgFull, reviewer, prefix, orgs, saving, errors,
  onChange, onClose, onSave, photos = [], onChangePhotos }) {
  const { t } = useTranslation();
  const viewing = mode === 'view';
  const { requestClose, prompt: discardPrompt } = useDiscardGuard(
    { form, photoCount: photos.length }, onClose, { active: !saving },
  );
  const roundOptions = lists.rounds?.length ? lists.rounds : ROUND_OPTIONS;
  const outcomeOptions = lists.outcomes?.length ? lists.outcomes : OUTCOME_OPTIONS;
  const reworkReasons = lists.reworkReasons?.length ? lists.reworkReasons
    : REWORK_REASON_OPTIONS.map((name) => ({ name, owner: REASON_OWNER_MAP[name] || '' }));
  const categoryOptions = lists.categories?.length ? lists.categories : CATEGORY_FALLBACK;
  const seasonOptions = lists.seasons?.length ? lists.seasons : SEASON_FALLBACK;
  const developerOptions = lists.developers || [];
  const designerOptions = lists.designers || [];
  const locationOptions = lists.locations || [];
  /* "HEVN" on a rework reason is the side that MADE the sample; on this track that is the partner. */
  const ownerLabel = (owner) => (owner === 'HEVN' ? orgName : owner);

  const normStyle = (v) => String(v || '').trim().toLowerCase();
  const creating = mode === 'new' && !!form.creating;
  const existingBase = mode === 'new'
    ? (rounds.find((r) => normStyle(r.styleItem) === normStyle(form.styleItem)
        && (!form.orgKey || r.orgKey === form.orgKey)) || null)
    : null;
  const creatingBase = mode === 'new' && !!normStyle(form.styleItem) && !existingBase;
  const preview = calcPreview(form);
  const duplicate = findDuplicateRound(form, rounds, form.sampleId);
  const set = (field) => (event) => onChange({ [field]: event.target.value });
  const setChecked = (field) => (event) => onChange({ [field]: event.target.checked });
  const internal = isInternalTrial(form);
  const setTrialMode = (nextInternal) => {
    if (nextInternal) {
      onChange({ trialReviewMode: 'internal', hincDesigner: '', roundDue: '', receivedDate: '', commentDate: '', outcome: '', reworkReason: '' });
    } else {
      onChange({ trialReviewMode: 'designer' });
    }
  };

  const [nrSearch, setNrSearch] = useState('');
  const [nrLocked, setNrLocked] = useState(mode === 'new' && !!form.baseId);
  const sampleChosen = mode !== 'new' || creating || nrLocked || !!String(form.styleItem || '').trim();

  const baseSamples = useMemo(() => {
    const map = new Map();
    rounds.forEach((round) => {
      if (!round.baseId || !round.styleItem) return;
      const entry = map.get(round.baseId) || { baseId: round.baseId, styleItem: round.styleItem, orgKey: round.orgKey, sampleIds: [] };
      entry.styleItem = round.styleItem;
      if (round.sampleId && !entry.sampleIds.includes(round.sampleId)) entry.sampleIds.push(round.sampleId);
      map.set(round.baseId, entry);
    });
    return [...map.values()];
  }, [rounds]);
  const nrSearchTrim = nrSearch.trim().toLowerCase();
  const nrResults = mode === 'new' && !creating && !nrLocked && nrSearchTrim
    ? baseSamples.filter((sample) => sample.styleItem.toLowerCase().includes(nrSearchTrim)
        || sample.baseId.toLowerCase().includes(nrSearchTrim)
        || sample.sampleIds.some((id) => String(id).toLowerCase().includes(nrSearchTrim))).slice(0, 8)
    : [];

  // Carries the sample-level fields over from the latest round, as the Sample Tracker's picker does.
  const selectExistingSample = (sample) => {
    const sampleRounds = rounds.filter((round) => round.baseId === sample.baseId);
    const latest = (key) => {
      for (let i = sampleRounds.length - 1; i >= 0; i -= 1) {
        const value = sampleRounds[i][key];
        if (value !== null && value !== undefined && String(value).trim() !== '') return value;
      }
      return '';
    };
    onChange({
      baseId: sample.baseId,
      styleItem: sample.styleItem,
      orgKey: sample.orgKey,
      category: latest('category'),
      season: latest('season'),
      developer: latest('developer'),
      location: latest('location'),
      hincDesigner: latest('hincDesigner'),
      developmentDue: latest('developmentDue'),
      option: latest('option'),
      round: '',
      requestDate: '',
      roundDue: '',
      rmReadyDate: '',
      materialReadyExpected: true,
      sampleFinishDate: '',
      receivedDate: '',
      commentDate: '',
      qty: '',
      outcome: '',
      reworkReason: '',
      remarks: '',
      trialReviewMode: 'designer',
    });
    setNrLocked(true);
    setNrSearch('');
  };
  const clearNrSelected = () => {
    setNrLocked(false);
    onChange({ baseId: '', styleItem: '' });
  };

  const selectedReasons = form.reworkReason ? form.reworkReason.split('; ').filter(Boolean) : [];
  const toggleReworkReason = (reason) => {
    const next = selectedReasons.includes(reason)
      ? selectedReasons.filter((item) => item !== reason)
      : [...selectedReasons, reason];
    onChange({ reworkReason: next.join('; ') });
  };
  const fills = t('ps.fills', { org: orgName });

  return (
    <div className="st-modal-overlay" role="dialog" aria-modal="true">
      {discardPrompt}
      <div className="st-modal">
        <div className="st-modal-head">
          <h3>{viewing ? <>{t('st.rmTitleView')} — {form.sampleId} <span className="pt-view-tag">{t('st.ptViewTag')}</span></>
            : mode === 'edit' ? t('st.rmTitleEdit') : mode === 'advance' ? t('st.rmTitleAdvance')
              : creating ? t('st.rmTitleNewSample') : t('st.rmTitleNew')}</h3>
          <button type="button" className="st-modal-close" onClick={requestClose} aria-label={t('xPartner.close')}>&times;</button>
        </div>
        <div className="st-modal-body">
          <fieldset className="pt-ro-fieldset" disabled={viewing}>

          {creating && (
            <div className="nr-bar nr-bar-trial">
              <div className="nr-bar-title">
                {t('ps.createTitle')}
                <span className="nr-bar-sub">{t('ps.createSub', { prefix })}</span>
              </div>
              {orgs.length > 1 && (
                <div className="nr-search-row">
                  <select value={form.orgKey || ''} onChange={set('orgKey')}>
                    <option value="">{t('ps.pickCompany')}</option>
                    {orgs.filter((o) => o.active).map((o) => <option key={o.orgKey} value={o.orgKey}>{o.orgName}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {mode === 'new' && !creating && (
            <div className={`nr-bar${sampleChosen ? '' : ' nr-bar-waiting'}`}>
              <div className="nr-bar-title">
                {t('st.rmPickSample')}<span className="req">*</span>
                <span className="nr-bar-sub">{t('st.rmPickSampleSub')}</span>
              </div>
              {!sampleChosen && <div className="nr-bar-first">{t('st.rmPickFirst')}</div>}
              {!nrLocked && (
                <>
                  <div className="nr-search-row">
                    <input autoFocus value={nrSearch} onChange={(event) => setNrSearch(event.target.value)} placeholder={t('ps.pickPh', { prefix })} />
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNrSearch('')}>{t('st.rmClear')}</button>
                  </div>
                  {!!nrResults.length && (
                    <div className="nr-results">
                      {nrResults.map((sample) => (
                        <button type="button" key={sample.baseId} className="nr-result-row" onClick={() => selectExistingSample(sample)}>
                          <strong>{sample.baseId}</strong> — {sample.styleItem}
                          {!!sample.sampleIds.length && (
                            <span className="nr-result-ids">{sample.sampleIds.slice(-4).join(' · ')}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {nrSearchTrim && !nrResults.length && <div className="rt-note">{t('ps.pickNone')}</div>}
                </>
              )}
              {nrLocked && (
                <div className="nr-selected">
                  <div>
                    <span className="nr-selected-label">{t('st.rmAddingTo')}</span>
                    <div className="nr-selected-value">{form.baseId} — {form.styleItem}</div>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={clearNrSelected}>{t('st.rmChange')}</button>
                </div>
              )}
            </div>
          )}

          {mode === 'advance' && (
            <div className="st-advance-note">
              <strong>{advancingFrom?.sampleId || advancingFrom?.baseId}</strong> {t('st.rmAdvanceNote')}
              <div className="st-advance-note-sub">{t('st.rmAdvanceNoteSub')}</div>
            </div>
          )}

          {duplicate && (
            <div className="dup-warn">
              {t('st.rmDupWarn', {
                sampleId: duplicate.sampleId,
                round: `${duplicate.round}${duplicate.option ? `-${duplicate.option}` : ''}`,
                outcome: duplicate.outcome || t('st.rmDupOpen'),
              })}
            </div>
          )}

          <div className="legend-bar">
            <span className="lbl">{t('st.rmOwnership')}</span>
            <span className="chip chip-hevn"><span className="dot" />{fills}</span>
            <span className="chip chip-both"><span className="dot" />{t('st.rmBothTeams')}</span>
            <span className="chip chip-auto"><span className="dot" />{t('st.rmAutoGen')}</span>
            <span className="legend-req"><span className="req">*</span> {t('st.rmMandatory')}</span>
          </div>

          <div className={`form-layout${sampleChosen ? '' : ' nr-gated'}`}>
            <div>
              <div className="own-section own-hevn">
                <div className="own-hd"><span>{t('st.rmSampleIdent')}</span><span className="owner-tag">{fills}</span></div>
                <div className="own-body">
                  <div className="fg3">
                    <Field label={t('st.rmStyleItem')} required
                           hint={creating ? t('ps.styleTypedHint') : t('st.rmStyleFromSample')}>
                      {creating
                        ? <input autoFocus value={form.styleItem} onChange={set('styleItem')} placeholder={t('st.trStylePh')} />
                        : <input value={roundStyleLabel(form)} disabled className="auto-field" placeholder={t('st.rmStylePlaceholder')} />}
                    </Field>
                    <Field label={t('st.rmBaseId')} hint={t('st.rmAutoAssigned')}>
                      <input value={form.baseId || (existingBase ? existingBase.baseId : t('xPartner.auto'))} disabled className="auto-field" />
                    </Field>
                    <Field label={t('st.rmSampleId')} hint={t('st.rmSampleIdHint')}>
                      <input value={form.sampleId || t('xPartner.auto')} disabled className="auto-field" />
                    </Field>
                    <Field label={t('st.rmRound')} required>
                      <select value={form.round} onChange={set('round')}>
                        {(roundOptions.includes(form.round) || !form.round
                          ? roundOptions
                          : [form.round, ...roundOptions]
                        ).map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    <Field label={t('st.rmOption')} hint={t('st.rmOptionHint')}>
                      <input value={form.option} onChange={set('option')} placeholder={t('st.rmOptionPh')} />
                    </Field>
                    <Field label={t('st.rmCategory')}>
                      <select value={form.category} onChange={set('category')}>
                        <option value="">—</option>
                        {(categoryOptions.includes(form.category) || !form.category ? categoryOptions : [form.category, ...categoryOptions])
                          .map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    <Field label={t('st.rmSeason')}>
                      <select value={form.season} onChange={set('season')}>
                        <option value="">—</option>
                        {(seasonOptions.includes(form.season) || !form.season ? seasonOptions : [form.season, ...seasonOptions])
                          .map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    <Field label={t('st.rmDevelopmentDue')} required hint={t('st.rmDevDueHint')}>
                      <input type="date" className="yellow-field" value={form.developmentDue} onChange={set('developmentDue')} />
                    </Field>
                  </div>
                </div>
              </div>

              {/* Photos belong to the Base ID: required when this save makes one, read-only otherwise. */}
              {mode === 'new' && (
                <div className={`own-section ${creatingBase ? 'own-hevn' : 'own-project'}`}>
                  <div className="own-hd">
                    <span>{t('st.rmBasePhotos')}</span>
                    <span className={`owner-tag ${creatingBase ? '' : 'owner-tag-dark'}`}>
                      {creatingBase ? t('st.rmPhotoRequired') : t('st.rmOptional')}
                    </span>
                  </div>
                  <div className="own-body">
                    <div className="pt-help" style={{ marginBottom: 8 }}>
                      {creatingBase
                        ? t('st.rmPhotoNewBase')
                        : existingBase
                          ? t('st.rmPhotoExistingBase', { baseId: existingBase.baseId })
                          : t('st.rmPhotoNoStyle')}
                    </div>
                    {creatingBase && (
                      <ProjectPhotos scope={PHOTO_SCOPE} apiPath={PHOTO_API} pending pendingPhotos={photos} onPendingChange={onChangePhotos} />
                    )}
                    {!creatingBase && existingBase && (
                      <ProjectPhotos scope={PHOTO_SCOPE} apiPath={PHOTO_API} ownerKey={existingBase.baseId} readOnly />
                    )}
                  </div>
                </div>
              )}

              <div className="own-section own-review">
                <div className="own-hd"><span>{t('st.rmReviewType')}</span><span className="owner-tag">{orgName}</span></div>
                <div className="own-body">
                  <label className="rt-row">
                    <input type="radio" name="reviewType" checked={!internal} onChange={() => setTrialMode(false)} />
                    {/* Jay, 2026-10-19: "Requires Buyer/DAC KR review" / "DAC VN internal review only" —
                        who reviews is the company's own setting (reviewer_label), not a word here. */}
                    <span>{reviewer ? t('ps.requiresReview', { reviewer }) : t('st.rmRequiresDesigner')}</span>
                  </label>
                  <label className="rt-row">
                    <input type="radio" name="reviewType" checked={internal} onChange={() => setTrialMode(true)} />
                    <span>{t('ps.internalOnly', { org: orgFull || orgName })}</span>
                  </label>
                  <div className="rt-note">
                    {internal ? t('ps.internalNote', { org: orgFull || orgName })
                      : t('ps.reviewerNote', { reviewer: reviewer || t('ps.reviewerDefault') })}
                  </div>
                </div>
              </div>

              {internal && (
                <div className="own-section own-review">
                  <div className="own-hd"><span>{t('ps.internalReview', { org: orgFull || orgName })}</span><span className="owner-tag">{orgName}</span></div>
                  <div className="own-body">
                    <Field label={t('st.rmRequestDate')} required hint={t('st.rmInternalReqHint')}>
                      <input type="date" value={form.requestDate} onChange={set('requestDate')} />
                    </Field>
                    <Field label={t('st.rmRemarks')}>
                      <input value={form.remarks} onChange={set('remarks')} placeholder={t('st.rmInternalRemarksPh')} />
                    </Field>
                  </div>
                </div>
              )}

              <div className="own-section own-hevn">
                {/* "Sample Plan", not "DAC Production" — Jay, 2026-10-19, for HEVN's form as well. */}
                <div className="own-hd"><span>{t('st.rmSamplePlan')}</span><span className="owner-tag">{fills}</span></div>
                <div className="own-body">
                  <div className="fg3">
                    {/* No designer on this form (the HINC card is gone), so the developer is the person. */}
                    <Field label={t('st.rmDeveloper')} required>
                      <input list="ps-developer-options" value={form.developer} onChange={set('developer')} placeholder={t('st.rmDeveloperPh')} />
                    </Field>
                    <Field label={t('ps.location')}>
                      <input list="ps-location-options" value={form.location} onChange={set('location')} placeholder={t('ps.locationPh')} />
                    </Field>
                    <Field label={t('st.rmRmReady')} required hint={t('st.rmRmReadyHint')}>
                      <input type="date" value={form.rmReadyDate} onChange={set('rmReadyDate')} />
                      <label className="expected-check">
                        <input type="checkbox" checked={!!form.materialReadyExpected} onChange={setChecked('materialReadyExpected')} />
                        {t('st.rmExpectedDate')}
                      </label>
                    </Field>
                    <Field label={t('st.rmSampleFinish')}>
                      <input type="date" value={form.sampleFinishDate} onChange={set('sampleFinishDate')} />
                    </Field>
                    <Field label={t('st.rmQty')}>
                      <input type="number" min="0" value={form.qty} onChange={set('qty')} />
                    </Field>
                  </div>
                  <div className="calc-summary">
                    <span className="calc-label">{t('st.rmAutoCalc')}</span>
                    &nbsp; {t('st.rmMakeDays')}: <b>{fmtDays(preview.makeDays)}</b>
                    &nbsp;&nbsp; {t('st.rmShipDays')}: <b>{fmtDays(preview.shipDays)}</b>
                    &nbsp;&nbsp; {t('st.rmRoundCycle')}: <b>{fmtDays(preview.roundCycleDays)}</b>
                    &nbsp;&nbsp; {t('st.rmDday')}: <b>{preview.dDay === '' ? '—' : dDayLabel(num(preview.dDay))}</b>
                  </div>
                </div>
              </div>

              {/* NO HINC DESIGN REVIEW CARD — Jay, 2026-10-19: "DAC VN doesn't need this. Please delete this
                  card." Its designer, request date, round due and received date went with it; the Review
                  Date stays, in Round Outcome below, because an outcome is decided at a review and the
                  rules (and the D-Day, which stops there) need its date. */}
              {!internal && (
              <div className="own-section own-both">
                <div className="own-hd"><span>{t('st.rmRoundOutcome')}</span><span className="owner-tag">{t('st.rmBothTeams')}</span></div>
                <div className="own-body">
                  <div className="fg3">
                    <Field label={t('st.rmRoundOutcome')} hint={t('st.rmOutcomeHint')}>
                      <select value={form.outcome} onChange={set('outcome')}>
                        <option value="">{t('st.rmNotSetYet')}</option>
                        {outcomeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    <Field label={t('st.rmReviewDate')} required={!!String(form.outcome || '').trim()}
                      hint={t('ps.reviewDateHint', { reviewer: reviewer || t('ps.reviewerDefault') })}>
                      <input type="date" value={form.commentDate} onChange={set('commentDate')} />
                    </Field>
                  </div>
                  <div className="outcome-desc" style={OUTCOME_DESC_STYLE[form.outcome] || OUTCOME_DESC_STYLE['']}>
                    {OUTCOME_DESCRIPTIONS[form.outcome] || t('st.rmOutcomePick')}
                  </div>
                  {form.outcome === 'Rework' && (
                    <div className="rework-box">
                      <div className="rework-label">{t('st.rmReworkTitle')}</div>
                      <div className="ff" style={{ marginBottom: 10 }}>
                        <span>{t('st.rmReworkReason')}<span className="req">*</span> <span className="hint" style={{ fontWeight: 400 }}>{t('st.rmSelectAll')}</span></span>
                        <div className="reason-cbs">
                          {reworkReasons.map(({ name, owner }) => (
                            <label className="cb-row" key={name}>
                              <span className="cb-row-left">
                                <input type="checkbox" checked={selectedReasons.includes(name)} onChange={() => toggleReworkReason(name)} />
                                <span>{name}</span>
                              </span>
                              <span className={`owner-tag ${reasonOwnerClass(owner)}`}>{ownerLabel(owner)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <Field label={t('st.rmRemarks')} required={form.outcome === 'Dropped'}>
                    <textarea rows={2} value={form.remarks} onChange={set('remarks')} placeholder={t('st.rmRoundRemarksPh')} />
                  </Field>
                </div>
              </div>
              )}
            </div>
          </div>
          </fieldset>
        </div>
        {!!errors.length && (
          <div className="st-alert error st-alert-foot">{errors.map((message) => <div key={message}>{message}</div>)}</div>
        )}
        {viewing ? (
          <div className="st-modal-foot st-form-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{t('st.viewClose')}</button>
          </div>
        ) : (
          <div className="st-modal-foot st-form-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
              {saving ? t('st.rmSaving') : mode === 'edit' ? t('st.rmSaveChanges')
                : mode === 'advance' ? t('st.rmSaveAdvance')
                  : creating ? t('st.trAddNewSample') : t('st.rmAddNewRound')}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={requestClose} disabled={saving}>
              {mode === 'advance' ? t('st.rmCancelAdvance') : t('st.rmCancel')}
            </button>
          </div>
        )}
      </div>
      <datalist id="ps-developer-options">{developerOptions.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="ps-designer-options">{designerOptions.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="ps-location-options">{locationOptions.map((option) => <option key={option} value={option} />)}</datalist>
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------------- */

export default function PartnerSamples() {
  const { t } = useTranslation();
  const { permissions = {} } = useAuth();
  const canEdit = permissions.partner_samples === 'edit';

  /* THE GUIDE — Max, 2026-10-19 ("thiếu cái tính năng take a tour hay guide á"). The Sample Tracker's own
     TourGuide over this screen's controls; a step whose control is not here (Create Sample for somebody
     who may only look, a sample row on an empty track) is skipped by TourGuide itself. Three steps carry
     their own words: creating is a typed style and a photo here, the filters have no designer, and the
     two tabs are worth one line. */
  const TOUR_STEPS = useMemo(() => [
    { selector: '.toolbar-actions .btn-create-sample', title: 'tour.stCreateT', body: 'ps.tourCreateB' },
    { selector: '.toolbar-actions .btn-new-round', title: 'tour.stRoundT', body: 'tour.stRoundB' },
    { selector: '.mv-kpi-section', title: 'tour.stCardsT', body: 'tour.stCardsB' },
    { selector: '.st-filter-bar', title: 'tour.stFiltersT', body: 'ps.tourFiltersB' },
    { selector: 'tbody tr.my-group-hd', title: 'tour.stRowT', body: 'tour.stRowB' },
    { selector: '.sample-subtabs', title: 'ps.tourTabsT', body: 'ps.tourTabsB' },
    { selector: '.btn-export-excel', title: 'tour.stExportT', body: 'tour.stExportB' },
    MANUAL_STEP,
  ], []);
  const [tourOpen, setTourOpen] = useState(false);
  const TOUR_SEEN = 'portalPartnerTourSeen';
  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(TOUR_SEEN, '1'); } catch { /* a private window still gets the tour */ }
  };

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sampleView, setSampleView] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [openRemarks, setOpenRemarks] = useState({});
  const [sampleSort, setSampleSort] = useState({ key: '', dir: 'asc' });
  const [sampleFilters, setSampleFilters] = useState(FILTERS_DEFAULT);
  const [sampleRowMode, setSampleRowMode] = useState('group');
  const [modal, setModal] = useState(null);         // { mode, form, current }
  const [modalErrors, setModalErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newRoundPhotos, setNewRoundPhotos] = useState([]);
  const [baseModal, setBaseModal] = useState(null);  // { baseId, styleItem, rounds, view }
  const [baseStyleDraft, setBaseStyleDraft] = useState('');
  useEffect(() => { setBaseStyleDraft(baseModal?.styleItem || ''); }, [baseModal]);
  useEscapeClose(() => setBaseModal(null), !!baseModal && !saving);

  const load = useCallback(async () => {
    try {
      setData(await call(''));
      setError('');
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  /* Once, unasked, when the screen has its data — NOT when it has rows, as the Sample Tracker waits for:
     DAC's first people open an empty track, and they are the ones the guide is for. */
  useEffect(() => {
    if (!data) return undefined;
    let seen = true;
    try { seen = !!localStorage.getItem(TOUR_SEEN); } catch { seen = true; }
    if (seen) return undefined;
    const id = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(id);
  }, [data]);

  const me = data?.me || {};
  const orgs = useMemo(() => data?.orgs || [], [data]);
  const lists = useMemo(() => data?.lists || {}, [data]);
  const rounds = useMemo(() => data?.rounds || [], [data]);
  const multiOrg = !me.external && orgs.length > 1;
  const orgName = me.external ? me.orgName : (orgs.length === 1 ? orgs[0].orgName : t('ps.partner'));
  const prefix = (me.external ? orgs.find((o) => o.orgKey === me.orgKey)?.idPrefix : orgs[0]?.idPrefix) || 'DAC';
  /* "DAC VN" is the company; "DAC" is how a form reads ("DAC fills"). */
  const shortName = String(orgName || '').split(' ')[0];
  const pageOrg = me.external ? orgs.find((o) => o.orgKey === me.orgKey) : (orgs.length === 1 ? orgs[0] : null);

  const enrichedRounds = useMemo(() => rounds.map((round) => ({ ...round, status: roundStatus(round) })), [rounds]);
  const groupedSamples = useMemo(() => groupSamplesOf(enrichedRounds).map((g) => ({
    ...g, orgKey: g.latest?.orgKey, orgName: g.latest?.orgName,
  })), [enrichedRounds]);

  const filterOptions = useMemo(() => {
    const unique = (rows, field) => [...new Set(rows.map((row) => row[field]).filter(Boolean))].sort();
    return {
      developers: lists.developers?.length ? lists.developers : unique(enrichedRounds, 'developer'),
      designers: lists.designers?.length ? lists.designers : unique(enrichedRounds, 'hincDesigner'),
      locations: lists.locations?.length ? lists.locations : unique(enrichedRounds, 'location'),
    };
  }, [lists, enrichedRounds]);

  /* Excel-style column filters, one slice per grid — the Sample Tracker's own machinery. */
  const [columnFilters, setColumnFilters] = useState({});
  const [openColumnFilter, setOpenColumnFilter] = useState(null);
  const [columnFilterAnchor, setColumnFilterAnchor] = useState(null);
  const tableFilters = (tableId) => columnFilters[tableId] || NO_COLUMN_FILTERS;
  const applyColumnFilter = (tableId) => (key, values, allValues) => {
    setColumnFilters((prev) => {
      const current = { ...(prev[tableId] || {}) };
      const isAll = allValues && allValues.length && values.length >= allValues.length;
      if (!values || !values.length || isAll) delete current[key]; else current[key] = values;
      const next = { ...prev };
      if (Object.keys(current).length) next[tableId] = current; else delete next[tableId];
      return next;
    });
    setOpenColumnFilter(null);
  };
  const clearColumnFilters = () => { setColumnFilters({}); setOpenColumnFilter(null); };
  const columnFilterProps = (tableId, getters, sourceRows) => (key) => {
    const active = tableFilters(tableId);
    const isOpen = !!openColumnFilter && openColumnFilter.table === tableId && openColumnFilter.key === key;
    return {
      filterValues: isOpen ? distinctColumnValues(sourceRows, key, active, getters) : undefined,
      filterSelected: active[key],
      filterOpen: isOpen,
      filterAnchor: columnFilterAnchor,
      onOpenFilter: (k, anchor) => {
        setColumnFilterAnchor(anchor);
        setOpenColumnFilter((prev) => (prev && prev.table === tableId && prev.key === k ? null : { table: tableId, key: k }));
      },
      onCloseFilter: () => setOpenColumnFilter(null),
      onApplyFilter: applyColumnFilter(tableId),
    };
  };

  /* The Sample Tracker's toolbar filter, less the SMP/TSMP split and the drill-through chips this screen has
     no card for — plus Company, when HEVN is looking at more than one partner. */
  const toolbarFilteredGroups = useMemo(() => {
    const search = sampleFilters.search.trim().toLowerCase();
    const isProductView = sampleView === 'product';
    return groupedSamples.filter((sample) => {
      const latest = sample.latest || {};
      const matchesSampleStatus = !sampleFilters.sampleStatus.length || sampleFilters.sampleStatus.some((want) => (
        isProductView
          ? sample.productViewStatus === want
          : want === 'In Progress'
            ? !['Confirmed', 'Rework', 'Dropped', 'On Hold'].includes(sample.status)
            : sample.status === want
      ));
      // the Sample Tracker's own rule, so the cards above count what this list shows (15_SampleTracker.jsx, Jay 29/10)
      const matchesRoundStatus = isProductView || !sampleFilters.roundStatus.length
        || sampleFilters.roundStatus.some((want) => sampleMatchesRoundStatus(sample, want));
      const matchesSearch = !search || [sample.baseId, sample.styleItem, latest.sampleId, latest.remarks]
        .some((value) => String(value || '').toLowerCase().includes(search));
      return matchesSampleStatus
        && matchesRoundStatus
        && (!sampleFilters.org.length || sampleFilters.org.includes(sample.orgName))
        && (!sampleFilters.developer.length || sample.rounds.some((round) => sampleFilters.developer.includes(round.developer)))
        && (isProductView || !sampleFilters.designer.length
            || sample.rounds.some((round) => sampleFilters.designer.includes(round.hincDesigner)))
        && (!sampleFilters.location.length || sampleFilters.location.includes(sample.location))
        && matchesSearch;
    });
  }, [groupedSamples, sampleFilters, sampleView]);

  const sampleColumnFilters = tableFilters('samples');
  const summaryColumnFilters = tableFilters('summary');
  const filteredSampleGroups = useMemo(() => {
    const rows = Object.keys(sampleColumnFilters).length
      ? toolbarFilteredGroups.filter((sample) => rowPassesColumnFilters(sample, sampleColumnFilters, SAMPLE_SORT_GETTERS))
      : toolbarFilteredGroups;
    return sortRows(rows, sampleSort, SAMPLE_SORT_GETTERS);
  }, [toolbarFilteredGroups, sampleSort, sampleColumnFilters]);
  const summarySampleGroups = useMemo(() => {
    const rows = Object.keys(summaryColumnFilters).length
      ? toolbarFilteredGroups.filter((sample) => rowPassesColumnFilters(sample, summaryColumnFilters, SAMPLE_SORT_GETTERS))
      : toolbarFilteredGroups;
    return sortRows(rows, sampleSort, SAMPLE_SORT_GETTERS);
  }, [toolbarFilteredGroups, sampleSort, summaryColumnFilters]);
  const toolbarFilteredRounds = useMemo(() => {
    const rows = [];
    toolbarFilteredGroups.forEach((sample) => sample.rounds.forEach((round) => rows.push(round)));
    return rows
      .filter((round) => !sampleFilters.designer.length || sampleFilters.designer.includes(round.hincDesigner))
      .filter((round) => !sampleFilters.developer.length || sampleFilters.developer.includes(round.developer))
      .filter((round) => !sampleFilters.roundStatus.length
        || sampleFilters.roundStatus.some((want) => roundMatchesStatus(round, want)));
  }, [toolbarFilteredGroups, sampleFilters]);
  const flatFilteredRounds = useMemo(() => {
    const rows = Object.keys(sampleColumnFilters).length
      ? toolbarFilteredRounds.filter((round) => rowPassesColumnFilters(round, sampleColumnFilters, ROUND_FILTER_GETTERS))
      : toolbarFilteredRounds;
    return sortRows(rows, sampleSort, ROUND_FILTER_GETTERS);
  }, [toolbarFilteredRounds, sampleColumnFilters, sampleSort]);

  const thFilter = columnFilterProps(
    'samples',
    sampleRowMode === 'rounds' ? ROUND_FILTER_GETTERS : SAMPLE_SORT_GETTERS,
    sampleRowMode === 'rounds' ? toolbarFilteredRounds : toolbarFilteredGroups,
  );
  const thFilterSummary = columnFilterProps('summary', SAMPLE_SORT_GETTERS, toolbarFilteredGroups);

  const isSampleFiltered = Object.values(sampleFilters).some((value) => (
    Array.isArray(value) ? value.length > 0 : String(value ?? '').trim() !== ''
  ));
  const totalSampleStats = useMemo(() => computeSampleStats(groupedSamples, enrichedRounds), [groupedSamples, enrichedRounds]);
  const filteredSampleStats = useMemo(() => computeSampleStats(
    filteredSampleGroups,
    sampleRowMode === 'rounds' ? flatFilteredRounds : filteredSampleGroups.flatMap((sample) => sample.rounds),
  ), [filteredSampleGroups, flatFilteredRounds, sampleRowMode]);
  const sampleStats = isSampleFiltered ? filteredSampleStats : totalSampleStats;
  const withTotalSub = (label, filteredValue, totalValue) => (
    isSampleFiltered && filteredValue !== totalValue ? t('xPartner.ofTotal', { label, total: totalValue }) : label
  );
  const onlySampleStatus = (s) => sampleFilters.sampleStatus.length === 1 && sampleFilters.sampleStatus[0] === s;
  const onlyRoundStatus = (s) => sampleFilters.roundStatus.length === 1 && sampleFilters.roundStatus[0] === s;

  const toggleSampleSort = (key, dir) => {
    if (dir) { setSampleSort({ key, dir }); setOpenColumnFilter(null); return; }
    setSampleSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  /* Every card is a filter — the Sample Tracker's quickFilter. */
  const quickFilter = (type) => {
    setSampleView('all');
    const ROUND_CARDS = ['roundInProgress', 'roundConfirmed', 'roundRework', 'roundDropped', 'roundOnHold', 'pending', 'waiting', 'inprod', 'totalRounds'];
    setSampleRowMode(ROUND_CARDS.includes(type) ? 'rounds' : 'group');
    setExpanded({});
    const base = { sampleStatus: [], roundStatus: [] };
    const pick = {
      sampleInProgress: { sampleStatus: ['In Progress'] }, sampleConfirmed: { sampleStatus: ['Confirmed'] },
      sampleRework: { sampleStatus: ['Rework'] }, sampleDropped: { sampleStatus: ['Dropped'] },
      sampleOnHold: { sampleStatus: ['On Hold'] }, roundInProgress: { roundStatus: ['In Progress'] },
      roundConfirmed: { roundStatus: ['Confirmed'] }, roundRework: { roundStatus: ['Rework'] },
      roundDropped: { roundStatus: ['Dropped'] }, roundOnHold: { roundStatus: ['On Hold'] },
      pending: { roundStatus: ['Pending Outcome'] }, samplePending: { roundStatus: ['Pending Outcome'] },
      waiting: { roundStatus: ['RM Waiting'] }, sampleWaiting: { roundStatus: ['RM Waiting'] },
      inprod: { roundStatus: ['In Production'] }, sampleInprod: { roundStatus: ['In Production'] },
    }[type];
    if (type === 'all' || type === 'totalRounds') setSampleFilters(FILTERS_DEFAULT);
    else if (pick) setSampleFilters((prev) => ({ ...prev, ...base, ...pick }));
  };

  /* ---- the round form ---- */
  const inheritFromSample = (baseId) => {
    const mine = rounds.filter((r) => r.baseId === baseId);
    const latest = (key) => {
      for (let i = mine.length - 1; i >= 0; i -= 1) {
        const value = mine[i][key];
        if (value !== null && value !== undefined && String(value).trim() !== '') return value;
      }
      return '';
    };
    return {
      orgKey: latest('orgKey'),
      category: latest('category'),
      season: latest('season'),
      developer: latest('developer'),
      location: latest('location'),
      hincDesigner: latest('hincDesigner'),
      developmentDue: latest('developmentDue'),
    };
  };
  const openNewRoundModal = (prefill = {}) => {
    setNewRoundPhotos([]);
    setModal({ mode: 'new', form: { ...emptyForm(), ...prefill } });
    setModalErrors([]);
  };
  const openCreateSampleModal = () => {
    setNewRoundPhotos([]);
    setModal({ mode: 'new', form: { ...emptyForm(), creating: true, orgKey: multiOrg ? '' : (orgs[0]?.orgKey || '') } });
    setModalErrors([]);
  };
  const openEditRoundModal = (round, view = false) => {
    setModal({ mode: view ? 'view' : 'edit', form: { ...emptyForm(), ...round } });
    setModalErrors([]);
  };
  const duplicateRound = (round) => {
    setModal({ mode: 'new', form: { ...emptyForm(), ...round, sampleRoundId: null, sampleId: '', outcome: '', reworkReason: '' } });
    setModalErrors([]);
  };
  const closeModal = () => {
    if (modal?.mode === 'advance') {
      setMessage(modal.current?.sampleId
        ? t('xPartner.cancelledAdvance', { id: modal.current.sampleId })
        : t('xPartner.cancelledAdvanceRound'));
    }
    setModal(null);
    setModalErrors([]);
  };
  const updateModalForm = (patch) => setModal((prev) => (prev ? { ...prev, form: { ...prev.form, ...patch } } : prev));
  const openAdvanceChainModal = (current) => {
    const sameSample = rounds.filter((round) => round.baseId && round.baseId === current.baseId && round.sampleId !== current.sampleId);
    setModal({
      mode: 'advance',
      current,
      form: {
        ...emptyForm(), ...current, sampleRoundId: null, sampleId: '',
        round: nextRoundLabel(current.round, sameSample), option: '',
        requestDate: '', roundDue: '', rmReadyDate: '', materialReadyExpected: true, sampleFinishDate: '',
        receivedDate: '', commentDate: '', outcome: '', reworkReason: '', remarks: '', trialReviewMode: 'designer',
      },
    });
    setModalErrors([]);
  };

  const handleSaveRound = async () => {
    if (!modal) return;
    /* The Sample Tracker's rules, but "a Developer OR a Designer" becomes "a Developer": this form has no
       designer since the HINC card came off (2026-10-19), so the designer half could only be satisfied by
       a value nobody can see. */
    const errors = validateForm(modal.form, modal.mode).filter((m) => m !== 'A Developer or a Designer is required');
    if (!String(modal.form.developer || '').trim()) errors.unshift(t('xPartner.developerRequired'));
    if (modal.mode === 'new' && modal.form.creating && multiOrg && !modal.form.orgKey) errors.push(t('ps.pickCompany'));
    if (modal.mode === 'new' && !newRoundPhotos.length) {
      const style = String(modal.form.styleItem || '').trim().toLowerCase();
      const known = rounds.some((r) => String(r.styleItem || '').trim().toLowerCase() === style
        && (!modal.form.orgKey || r.orgKey === modal.form.orgKey));
      if (style && !known) errors.push(t('xPartner.photoRequired'));
    }
    if (errors.length) { setModalErrors(errors); return; }
    /* A round already stored as Advance is corrected as an edit; only a change TO Advance opens the
       next round — the Sample Tracker's own rule (Max, 2026-10-22). */
    const wasAdvance = modal.mode === 'edit' && String(rounds.find((r) => r.sampleRoundId
      && r.sampleRoundId === modal.form.sampleRoundId)?.outcome || '').trim() === 'Advance';
    if (modal.mode !== 'advance' && !wasAdvance && String(modal.form.outcome || '').trim() === 'Advance') {
      openAdvanceChainModal(modal.form);
      return;
    }
    setSaving(true);
    setModalErrors([]);
    try {
      const isAdvance = modal.mode === 'advance';
      const isEdit = modal.mode === 'edit';
      const out = isAdvance
        ? await call('/rounds/advance-chain', 'POST', { current: modal.current, next: modal.form })
        : isEdit
          ? await call(`/rounds/${modal.form.sampleRoundId}`, 'PUT', modal.form)
          : await call('/rounds', 'POST', newRoundPhotos.length
            ? { ...modal.form, photos: newRoundPhotos.map((ph) => ({ mimeType: ph.mimeType, dataBase64: ph.dataBase64, fileName: ph.fileName })) }
            : modal.form);
      setData((prev) => ({ ...prev, rounds: out.rounds || [] }));
      const addedId = out.round?.sampleId || t('xPartner.round');
      setMessage(isAdvance
        ? t('xPartner.advanced', { from: out.advancedSampleId, to: out.newSampleId })
        : isEdit ? t('xPartner.roundUpdated')
          : newRoundPhotos.length
            ? t('xPartner.roundAddedPhotos', { id: addedId, n: newRoundPhotos.length, base: out.round?.baseId || t('xPartner.theBaseId') })
            : t('xPartner.roundAdded', { id: addedId }));
      setNewRoundPhotos([]);
      setModal(null);
      setModalErrors([]);
      // A new name, room or person joins the suggestion lists.
      load();
    } catch (err) {
      setModalErrors(String(err.message || '').split('\n'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRound = async (round) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('xPartner.confirmDelete', { id: round.sampleId }))) return;
    setSaving(true);
    setError('');
    try {
      const out = await call(`/rounds/${round.sampleRoundId}`, 'DELETE');
      setData((prev) => ({ ...prev, rounds: out.rounds || [] }));
      setMessage(t('xPartner.roundDeleted'));
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleRenameSample = async (baseId, current, nextName) => {
    const styleItem = String(nextName ?? '').trim();
    if (!styleItem || styleItem === current) return;
    setSaving(true);
    setError('');
    try {
      const out = await call(`/bases/${encodeURIComponent(baseId)}`, 'PUT', { styleItem });
      setData((prev) => ({ ...prev, rounds: out.rounds || [] }));
      setMessage(t('xPartner.renamed', { id: baseId, name: styleItem, n: out.renamed }));
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  /* ---- Excel: the Sample Tracker's export, its columns less Project SKU ID ---- */
  /* An export to read (nothing imports it back), so its headings follow the reader's language; the server colours
     the Status column by its position (statusColumn), not by its heading. */
  const EXPORT_COLUMNS = ['#', ...['baseId', 'styleItem', 'round', 'season', 'location', 'developer',
    'requestDate', 'developmentDue', 'rmReady', 'sampleFinish', 'comment',
    'qty', 'rounds', 'status', 'dDay', 'daysWaiting', 'remarks'].map((k) => t(`xPartner.col.${k}`))];
  const handleExport = async () => {
    const dash = (v) => (v === '' || v === null || v === undefined ? '-' : String(v));
    const roundValues = (round, n) => [
      n, round.sampleId, roundStyleLabel(round), round.round, round.season, round.location, round.developer,
      fmt(round.requestDate), fmt(round.developmentDue), fmt(round.rmReadyDate),
      fmt(round.sampleFinishDate), fmt(round.commentDate),
      fmt(round.qty), '', round.status || round.outcome || 'Pending', dDayLabel(round.dDay), '', fmt(round.remarks),
    ].map(dash);
    const exportRows = [];
    if (sampleRowMode === 'rounds') {
      flatFilteredRounds.forEach((round, i) => exportRows.push({ type: 'round', values: roundValues(round, i + 1) }));
    } else {
      filteredSampleGroups.forEach((sample, i) => {
        const latest = sample.latest || {};
        exportRows.push({
          type: 'base',
          values: [
            i + 1, sample.baseId, sample.styleItem, latest.round, latest.season, sample.location, sample.developer,
            fmt(latest.requestDate), fmt(sample.developmentDue), fmt(latest.rmReadyDate),
            fmt(latest.sampleFinishDate), fmt(latest.commentDate),
            fmt(latest.qty), sample.roundCount, sample.latestStatus, dDayLabel(sample.dDay), '', fmt(latest.remarks),
          ].map(dash),
        });
        if (expanded[sample.baseId]) sample.rounds.forEach((round) => exportRows.push({ type: 'round', values: roundValues(round, '') }));
      });
    }
    if (!exportRows.length) { setMessage(t('xPartner.noRowsToExport')); return; }
    const labelOf = (list, allLabel) => (list.includes(MULTI_FILTER_NONE) ? t('xPartner.noneSelected') : list.length ? list.join(', ') : allLabel);
    const filters = [
      t('xPartner.filterSample', { v: labelOf(sampleFilters.sampleStatus, t('xPartner.allSamples')) }),
      t('xPartner.filterRound', { v: labelOf(sampleFilters.roundStatus, t('xPartner.allRounds')) }),
      t('xPartner.filterDeveloper', { v: labelOf(sampleFilters.developer, t('xPartner.allDevelopers')) }),
      t('xPartner.filterLocation', { v: labelOf(sampleFilters.location, t('xPartner.allLocations')) }),
    ];
    if (sampleFilters.search.trim()) filters.push(t('xPartner.filterSearch', { v: sampleFilters.search.trim() }));
    const stamp = fmtDateTimeVN(new Date(), '');
    const fileStamp = stamp.replace(/[: ]/g, '-');
    const filename = `${prefix}_All_Samples_${fileStamp}.xlsx`;
    try {
      setMessage(t('xPartner.preparingExport'));
      const response = await fetch(`${API}/export-xlsx`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          title: t('xPartner.exportTitle', { org: orgName }), generated: stamp, filters, columns: EXPORT_COLUMNS, rows: exportRows,
          filename, widths: EXPORT_WIDTHS, statusColumn: 15, sheetName: t('xPartner.allSamples'),
        }),
      });
      if (!response.ok) {
        let msg = t('xPartner.exportFailedStatus', { status: response.status });
        try { const j = await response.json(); if (j.error) msg = j.error; } catch (e) { /* non-JSON */ }
        throw new Error(msg);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
      setMessage(t('xPartner.excelExported'));
    } catch (err) { setError(err.message || t('xPartner.exportFailed')); }
  };

  /* ---- the grids ---- */
  const roundActions = (round) => (
    <div className="action-icons">
      <IconButton variant="view" title={t('st.viewRound')} onClick={() => openEditRoundModal(round, true)}><Eye size={14} /></IconButton>
      {canEdit && <IconButton variant="edit" title={t('xPartner.editRound')} onClick={() => openEditRoundModal(round)}><Pencil size={14} /></IconButton>}
      {canEdit && <IconButton variant="duplicate" title={t('xPartner.duplicateRound')} onClick={() => duplicateRound(round)}><Copy size={14} /></IconButton>}
      {canEdit && <IconButton variant="delete" title={t('xPartner.deleteRound')} onClick={() => handleDeleteRound(round)}><Trash2 size={14} /></IconButton>}
    </div>
  );
  const roundCells = (round, idCell, no) => (
    <>
      <td />
      <td className="pt-col-no">{no}</td>
      {idCell}
      <td>{roundStyleLabel(round)}</td>
      <td>{round.round}</td>
      <td>{round.season || '—'}</td>
      <td>{round.location || '—'}</td>
      <td>{round.developer || '—'}</td>
      <td>{fmt(round.requestDate)}</td>
      <td>{fmt(round.developmentDue)}</td>
      <td>{fmt(round.rmReadyDate)}</td>
      <td>{fmt(round.sampleFinishDate)}</td>
      <td>{fmt(round.commentDate)}</td>
      <td>{fmt(round.qty)}</td>
      <td>-</td>
      <td><span className={`pill ${outcomePillClass(roundStatus(round))}`}>{valueLabel(roundStatus(round))}</span></td>
      <td className={dDayClass(round.dDay, round.dDayDone)}>{dDayLabel(round.dDay)}</td>
      <td className="st-remarks" title={round.remarks || ''}>{fmt(round.remarks)}</td>
      <td>{roundActions(round)}</td>
    </>
  );

  const renderSampleTable = (rows) => (
    <div className="twrap data-xscroll sample-data-wrap">
      <table className="sample-scroll-table ps-scroll-table">
        <colgroup>{SAMPLE_COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
        <thead>
          <tr>
            <th />
            <th>#</th>
            <SortTh label={t('st.colBaseId')} sortKey="baseId" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('baseId')} />
            <SortTh label={t('st.colStyleItem')} sortKey="styleItem" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('styleItem')} />
            <SortTh label={t('st.colRound')} sortKey="round" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('round')} />
            <SortTh label={t('st.colSeason')} sortKey="season" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('season')} />
            <SortTh label={t('ps.location')} sortKey="location" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('location')} />
            <SortTh label={t('st.colDeveloper')} sortKey="developer" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('developer')} />
            <SortTh label={t('st.colRequestDate')} sortKey="requestDate" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('requestDate')} />
            <SortTh label={t('st.colDevelopmentDue')} sortKey="developmentDue" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('developmentDue')} />
            <SortTh label={t('st.colRmReady')} sortKey="rmReadyDate" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('rmReadyDate')} />
            <SortTh label={t('st.colSampleFinish')} sortKey="sampleFinishDate" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('sampleFinishDate')} />
            <SortTh label={t('st.colComment')} sortKey="commentDate" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('commentDate')} />
            <SortTh label={t('st.colQty')} sortKey="qty" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('qty')} />
            <SortTh label={t('st.colRounds')} sortKey="roundCount" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('roundCount')} />
            <SortTh label={t('st.colStatus')} sortKey="status" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('status')} />
            <SortTh label={t('st.colDday')} hint={t('st.colDdayHint')} sortKey="dDay" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('dDay')} />
            <th>{t('st.colRemarks')}</th>
            <th>{t('st.colActions')}</th>
          </tr>
        </thead>
        <tbody>
          {sampleRowMode === 'rounds' && flatFilteredRounds.map((round, i) => (
            <tr className="my-round-row" key={round.sampleRoundId}>
              {roundCells(round, <td className="idcell"><strong>{round.sampleId}</strong></td>, i + 1)}
            </tr>
          ))}
          {sampleRowMode === 'rounds' && !flatFilteredRounds.length && <tr><td colSpan={COLS} className="st-empty">{t('xPartner.noRounds')}</td></tr>}
          {sampleRowMode === 'group' && rows.map((sample, si) => {
            const isOpen = expanded[sample.baseId];
            const latest = sample.latest || {};
            const latestRemarks = [...new Set((sample.rounds || [])
              .filter((r) => String(r.round || '') === String(latest.round || ''))
              .map((r) => String(r.remarks || '').trim())
              .filter(Boolean))];
            return (
              <React.Fragment key={sample.baseId}>
                <tr className="my-group-hd" onClick={() => setExpanded((prev) => ({ ...prev, [sample.baseId]: !prev[sample.baseId] }))}>
                  <td>{isOpen ? '▾' : '▸'}</td>
                  <td className="pt-col-no">{si + 1}</td>
                  <td className="idcell"><strong>{sample.baseId}</strong></td>
                  <td className="st-name-cell" title={sample.styleItem || ''}><strong>{sample.styleItem}</strong></td>
                  <td>{latest.round || '—'}</td>
                  <td>{latest.season || '—'}</td>
                  <td>{sample.location || '—'}</td>
                  <td>{sample.developer || '—'}</td>
                  <td>{fmt(latest.requestDate)}</td>
                  <td>{fmt(sample.developmentDue)}</td>
                  <td>{fmt(latest.rmReadyDate)}</td>
                  <td>{fmt(latest.sampleFinishDate)}</td>
                  <td>{fmt(latest.commentDate)}</td>
                  <td>—</td>
                  <td>{sample.roundCount}</td>
                  <td><span className={`pill ${outcomePillClass(sample.latestStatus)}`}>{valueLabel(sample.latestStatus)}</span></td>
                  <td className={dDayClass(sample.dDay, sample.dDayDone)}>{dDayLabel(sample.dDay)}</td>
                  <td
                    className="st-remarks"
                    title={latestRemarks.join('\n') || ''}
                    onClick={(event) => {
                      if (latestRemarks.length < 2) return;
                      event.stopPropagation();
                      setOpenRemarks((prev) => ({ ...prev, [sample.baseId]: !prev[sample.baseId] }));
                    }}
                  >
                    {latestRemarks.length ? (
                      <div className={`st-remark-stack${openRemarks[sample.baseId] ? ' on' : ''}`}>
                        {latestRemarks.map((line, n) => <div key={n}>{line}</div>)}
                        {latestRemarks.length > 1 && !openRemarks[sample.baseId] && (
                          <span className="st-remark-more">+{latestRemarks.length - 1}</span>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                  <td onClick={(event) => event.stopPropagation()}>
                    {canEdit && (
                      <IconButton variant="add" title={t('xPartner.addRound')} onClick={() => openNewRoundModal({
                        styleItem: sample.styleItem, baseId: sample.baseId, ...inheritFromSample(sample.baseId),
                      })}><Plus size={14} /></IconButton>
                    )}
                    <IconButton variant="view" title={t('st.viewBase')} onClick={() => setBaseModal({ baseId: sample.baseId, styleItem: sample.styleItem || '', rounds: sample.roundCount, view: true })}><Eye size={14} /></IconButton>
                    {canEdit && (
                      <IconButton variant="edit" title={t('xPartner.editBase')} onClick={() => setBaseModal({ baseId: sample.baseId, styleItem: sample.styleItem || '', rounds: sample.roundCount })}><Pencil size={14} /></IconButton>
                    )}
                  </td>
                </tr>
                {isOpen && sample.rounds.map((round) => (
                  <tr className="my-round-row" key={round.sampleRoundId}>
                    {roundCells(round, <td>{round.sampleId}</td>, '')}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {sampleRowMode === 'group' && !rows.length && (
            <tr><td colSpan={COLS} className="st-empty">{rounds.length ? t('xPartner.noData') : t('ps.empty', { org: shortName })}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderProductView = (rows) => (
    <div className="twrap data-xscroll sample-data-wrap">
      <table className="product-view-table">
        <thead>
          <tr>
            <SortTh label={t('st.colBaseId')} sortKey="baseId" sort={sampleSort} onSort={toggleSampleSort} {...thFilterSummary('baseId')} />
            <SortTh label={t('st.colStyleItem')} sortKey="styleItem" sort={sampleSort} onSort={toggleSampleSort} {...thFilterSummary('styleItem')} />
            <SortTh label={t('ps.location')} sortKey="location" sort={sampleSort} onSort={toggleSampleSort} {...thFilterSummary('location')} />
            <SortTh label={t('st.colDeveloper')} sortKey="developer" sort={sampleSort} onSort={toggleSampleSort} {...thFilterSummary('developer')} />
            <SortTh label={t('st.colDevelopmentDue')} sortKey="developmentDue" sort={sampleSort} onSort={toggleSampleSort} {...thFilterSummary('developmentDue')} />
            <SortTh label={t('st.colDday')} hint={t('st.colDdayHint')} sortKey="dDay" sort={sampleSort} onSort={toggleSampleSort} {...thFilterSummary('dDay')} />
            <SortTh label={t('st.colRounds')} sortKey="roundCount" sort={sampleSort} onSort={toggleSampleSort} {...thFilterSummary('roundCount')} />
            <SortTh label={t('st.colRework')} sortKey="rework" sort={sampleSort} onSort={toggleSampleSort} {...thFilterSummary('rework')} />
            <SortTh label={t('st.colStatus')} sortKey="productStatus" sort={sampleSort} onSort={toggleSampleSort} {...thFilterSummary('productStatus')} />
            {[1, 2, 3, 4, 5].map((n) => <th key={n}>{`R${n}`}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((sample) => (
            <tr key={sample.baseId} className={sample.productViewStatus !== 'In Progress' ? 'past' : ''}>
              <td className="idcell"><strong>{sample.baseId}</strong></td>
              <td>{sample.styleItem}</td>
              <td>{sample.location || '—'}</td>
              <td>{sample.developer || '—'}</td>
              <td>{fmt(sample.developmentDue)}</td>
              <td className={dDayClass(sample.dDay, sample.dDayDone)}>{dDayLabel(sample.dDay)}</td>
              <td className={sample.roundCount >= 4 ? 'red' : ''}>{sample.roundCount}</td>
              <td>{sample.rework > 0 ? <span className="red">{sample.rework}</span> : sample.rework}</td>
              <td><span className={`pill ${productStatusPillClass(sample.productViewStatus)}`}>{valueLabel(sample.productViewStatus)}</span></td>
              {[0, 1, 2, 3, 4].map((idx) => {
                const round = sample.rounds[idx];
                const short = round ? SHORT_OUTCOME[round.outcome] : '';
                return <td key={idx}>{short ? <span className={`pill ${outcomePillClass(round.outcome)}`}>{short}</span> : null}</td>;
              })}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan="14" className="st-empty">{t('xPartner.noData')}</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const clearAll = () => {
    setSampleFilters(FILTERS_DEFAULT); setSampleSort({ key: '', dir: 'asc' }); setSampleRowMode('group');
    setExpanded({}); clearColumnFilters();
  };

  const renderSample = () => (
    <section className="screen active">
      {/* No sentence under the title (Max, 2026-10-18: "không cần dòng này"). */}
      <div className="screen-title"><h2>{sampleView === 'product' ? t('st.titleSummary') : t('ps.titleFor', { org: shortName })}</h2></div>
      {sampleView === 'all' && (
        <div className="mv-kpi-section pt-kpi">
          <div className="mv-kpi-title">{t('st.groupSample')}</div>
          <div className="mv-kpi-row">
            <Kpi label={t('st.totalSamples')} value={sampleStats.totalSamples} active={!isSampleFiltered && sampleRowMode === 'group'} sub={withTotalSub(t('st.totalSamplesSub'), sampleStats.totalSamples, totalSampleStats.totalSamples)} onClick={() => quickFilter('all')} />
            <Kpi label={t('st.inProgressSamples')} value={sampleStats.sampleInProgress} tone="kv-pend" active={onlySampleStatus('In Progress')} onClick={() => quickFilter('sampleInProgress')} />
            <Kpi label={t('st.confirmedSamples')} value={sampleStats.sampleConfirmed} tone="kv-conf" active={onlySampleStatus('Confirmed')} onClick={() => quickFilter('sampleConfirmed')} />
            <Kpi label={t('st.reworkSamples')} value={sampleStats.sampleRework} tone="kv-rew" active={onlySampleStatus('Rework')} onClick={() => quickFilter('sampleRework')} />
            <Kpi label={t('st.droppedSamples')} value={sampleStats.sampleDropped} tone="kv-drop" active={onlySampleStatus('Dropped')} onClick={() => quickFilter('sampleDropped')} />
            <Kpi label={t('st.onHoldSamples')} value={sampleStats.sampleOnHold} tone="kv-hold" active={onlySampleStatus('On Hold')} onClick={() => quickFilter('sampleOnHold')} />
            <Kpi label={t('st.pendingOutcome')} value={sampleStats.samplePendingOutcome} tone="kv-pend split-start" active={onlyRoundStatus('Pending Outcome') && sampleRowMode === 'group'} sub={withTotalSub(t('st.pendingOutcomeSub'), sampleStats.samplePendingOutcome, totalSampleStats.samplePendingOutcome)} onClick={() => quickFilter('samplePending')} />
            <Kpi label={t('st.rmWaiting')} value={sampleStats.sampleWaiting} tone="kv-wait" active={onlyRoundStatus('RM Waiting') && sampleRowMode === 'group'} sub={withTotalSub(t('st.rmWaitingSub'), sampleStats.sampleWaiting, totalSampleStats.sampleWaiting)} onClick={() => quickFilter('sampleWaiting')} />
            <Kpi label={t('st.inProduction')} value={sampleStats.sampleInProduction} tone="kv-conf" active={onlyRoundStatus('In Production') && sampleRowMode === 'group'} sub={withTotalSub(t('st.inProductionSub'), sampleStats.sampleInProduction, totalSampleStats.sampleInProduction)} onClick={() => quickFilter('sampleInprod')} />
          </div>
        </div>
      )}
      {sampleView === 'all' && (
        <div className="mv-kpi-section pt-kpi">
          <div className="mv-kpi-title">{t('st.groupRounds')}</div>
          <div className="mv-kpi-row">
            <Kpi label={t('st.totalRounds')} value={sampleStats.totalRounds} tone="kv-rnd" active={!isSampleFiltered && sampleRowMode === 'rounds'} sub={withTotalSub(t('st.totalRoundsSub'), sampleStats.totalRounds, totalSampleStats.totalRounds)} onClick={() => quickFilter('totalRounds')} />
            <Kpi label={t('st.inProgressRounds')} value={sampleStats.roundInProgress} tone="kv-pend" active={onlyRoundStatus('In Progress')} onClick={() => quickFilter('roundInProgress')} />
            <Kpi label={t('st.confirmedRounds')} value={sampleStats.roundConfirmed} tone="kv-conf" active={onlyRoundStatus('Confirmed')} onClick={() => quickFilter('roundConfirmed')} />
            <Kpi label={t('st.reworkRounds')} value={sampleStats.roundRework} tone="kv-rew" active={onlyRoundStatus('Rework')} onClick={() => quickFilter('roundRework')} />
            <Kpi label={t('st.droppedRounds')} value={sampleStats.roundDropped} tone="kv-drop" active={onlyRoundStatus('Dropped')} onClick={() => quickFilter('roundDropped')} />
            <Kpi label={t('st.onHoldRounds')} value={sampleStats.roundOnHold} tone="kv-hold" active={onlyRoundStatus('On Hold')} onClick={() => quickFilter('roundOnHold')} />
            <Kpi label={t('st.pendingOutcome')} value={sampleStats.pendingOutcome} tone="kv-pend split-start" active={onlyRoundStatus('Pending Outcome') && sampleRowMode === 'rounds'} sub={withTotalSub(t('st.pendingOutcomeSub'), sampleStats.pendingOutcome, totalSampleStats.pendingOutcome)} onClick={() => quickFilter('pending')} />
            <Kpi label={t('st.rmWaiting')} value={sampleStats.waiting} tone="kv-wait" active={onlyRoundStatus('RM Waiting') && sampleRowMode === 'rounds'} sub={withTotalSub(t('st.rmWaitingSub'), sampleStats.waiting, totalSampleStats.waiting)} onClick={() => quickFilter('waiting')} />
            <Kpi label={t('st.inProduction')} value={sampleStats.inProduction} tone="kv-conf" active={onlyRoundStatus('In Production') && sampleRowMode === 'rounds'} sub={withTotalSub(t('st.inProductionSub'), sampleStats.inProduction, totalSampleStats.inProduction)} onClick={() => quickFilter('inprod')} />
          </div>
        </div>
      )}
      {sampleView === 'all' && (
        <div className="toolbar st-filter-bar">
          {multiOrg && (
            <MultiFilter value={sampleFilters.org} onChange={(value) => setSampleFilters({ ...sampleFilters, org: value })} allLabel={t('ps.allCompanies')} options={orgs.map((o) => o.orgName)} />
          )}
          <MultiFilter value={sampleFilters.sampleStatus} onChange={(value) => setSampleFilters({ ...sampleFilters, sampleStatus: value })} allLabel={t('st.tabAll')} options={['In Progress', 'Confirmed', 'Rework', 'Dropped', 'On Hold']} labelOf={valueLabel} />
          <MultiFilter value={sampleFilters.roundStatus} onChange={(value) => setSampleFilters({ ...sampleFilters, roundStatus: value })} allLabel={t('st.allRounds')} options={['In Progress', 'RM Waiting', 'In Production', 'Pending Outcome', 'Advance', 'Rework', 'Confirmed', 'On Hold', 'Dropped']} labelOf={valueLabel} />
          <MultiFilter value={sampleFilters.developer} onChange={(value) => setSampleFilters({ ...sampleFilters, developer: value })} allLabel={t('st.allDevelopers')} options={filterOptions.developers} />
          <MultiFilter value={sampleFilters.location} onChange={(value) => setSampleFilters({ ...sampleFilters, location: value })} allLabel={t('ps.allLocations')} options={filterOptions.locations} />
          <input value={sampleFilters.search} onChange={(event) => setSampleFilters({ ...sampleFilters, search: event.target.value })} placeholder={t('st.searchPlaceholder')} />
          <button type="button" className="btn btn-ghost btn-sm" onClick={clearAll}>{t('st.clear')}</button>
          <div className="toolbar-actions">
            {canEdit && (
              <>
                <button type="button" className="btn btn-primary btn-sm btn-green btn-create-sample" onClick={openCreateSampleModal}>{t('st.csButton')}</button>
                <button type="button" className="btn btn-primary btn-sm btn-green btn-new-round" onClick={() => openNewRoundModal()}>{t('st.newRound')}</button>
              </>
            )}
            {/* The Sample Tracker's own Guide, last and quiet — for everybody who can open the screen. */}
            <button type="button" className="btn btn-sm btn-tour" title={t('st.tourReopen')} onClick={() => setTourOpen(true)}>
              {t('st.tourButton')}
            </button>
            {/* The track's own manual (Jay, 2026-10-19: "just upload in Portal"), beside the Guide, for everybody who
                can open the screen — since 01/11 the portal's Manual button: over the screen on a click, a tab on a
                right-click, in the portal's language. */}
            <ManualButton manual="partner" />
          </div>
        </div>
      )}
      {sampleView === 'product' && (
        <div className="toolbar">
          <MultiFilter value={sampleFilters.sampleStatus} onChange={(value) => setSampleFilters({ ...sampleFilters, sampleStatus: value })} allLabel={t('st.allStatus')} options={['In Progress', 'Confirmed', 'Dropped', 'On Hold']} labelOf={valueLabel} />
          <MultiFilter value={sampleFilters.location} onChange={(value) => setSampleFilters({ ...sampleFilters, location: value })} allLabel={t('ps.allLocations')} options={filterOptions.locations} />
          <MultiFilter value={sampleFilters.developer} onChange={(value) => setSampleFilters({ ...sampleFilters, developer: value })} allLabel={t('st.allDevelopers')} options={filterOptions.developers} />
          <input value={sampleFilters.search} onChange={(event) => setSampleFilters({ ...sampleFilters, search: event.target.value })} placeholder={t('xPartner.searchSample')} />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSampleFilters(FILTERS_DEFAULT)}>{t('st.clear')}</button>
        </div>
      )}
      {sampleView === 'all' && (
        <div className="pg-bar">
          {sampleRowMode === 'group' ? (
            <>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExpanded(Object.fromEntries(filteredSampleGroups.map((row) => [row.baseId, true])))}>{t('st.expandAll')}</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExpanded({})}>{t('st.collapseAll')}</button>
            </>
          ) : (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSampleRowMode('group'); setExpanded({}); }}>{t('xPartner.backToBase')}</button>
          )}
          <button type="button" className="btn btn-ghost btn-sm btn-export-excel" onClick={handleExport}><Download size={14} /> {t('st.exportExcel')}</button>
        </div>
      )}
      {sampleView === 'all' && renderSampleTable(filteredSampleGroups)}
      {sampleView === 'product' && renderProductView(summarySampleGroups)}
    </section>
  );

  return (
    <div className="st-page ps-page">
      <Header />
      <main className="st-shell sample-portal-shell">
        <div className="sample-app">
          <div className="sample-subtabs view-tabs show" role="tablist">
            {sampleTabs.map((tab) => (
              <button type="button" role="tab" key={tab.id} aria-selected={sampleView === tab.id} className={sampleView === tab.id ? 'active' : ''} onClick={() => setSampleView(tab.id)}>
                <TabIcon k={`st-${tab.id}`} />{t(tab.label)}
                {tab.id === 'all' && <span className="st-tab-count">{totalSampleStats.totalSamples}</span>}
              </button>
            ))}
          </div>
          {(message || error) && <div className={`st-alert ${error ? 'error' : 'success'}`}>{error || message}</div>}
          {!data && !error ? <div className="st-empty">{t('ps.loading')}</div> : renderSample()}
        </div>
      </main>

      <TourGuide steps={TOUR_STEPS} open={tourOpen} onClose={closeTour} onFinish={closeTour} />

      {modal && (
        <PartnerRoundModal
          mode={modal.mode}
          advancingFrom={modal.current}
          form={modal.form}
          lists={lists}
          rounds={rounds}
          orgName={shortName}
          orgFull={orgName}
          reviewer={pageOrg?.reviewerLabel || ''}
          prefix={prefix}
          orgs={multiOrg ? orgs : []}
          saving={saving}
          errors={modalErrors}
          onChange={updateModalForm}
          onClose={closeModal}
          onSave={handleSaveRound}
          photos={newRoundPhotos}
          onChangePhotos={setNewRoundPhotos}
        />
      )}

      {baseModal && (
        <div className="st-modal-overlay" role="dialog" aria-modal="true">
          <div className="st-modal st-modal-sm">
            <div className="st-modal-head">
              <h3>{baseModal.baseId}{baseModal.view && <span className="pt-view-tag">{t('st.ptViewTag')}</span>}</h3>
              <button type="button" className="st-modal-close" onClick={() => setBaseModal(null)} aria-label={t('xPartner.close')}>&times;</button>
            </div>
            <div className="st-modal-body">
              <div className="pt-help" style={{ marginBottom: 10 }}>{t('st.bmHint', { rounds: baseModal.rounds })}</div>
              <Field label={t('st.rmStyleItem')} required hint={t('st.bmStyleHint')}>
                <input
                  value={baseModal.view ? baseModal.styleItem : baseStyleDraft}
                  onChange={(e) => setBaseStyleDraft(e.target.value)}
                  placeholder={baseModal.styleItem}
                  readOnly={!!baseModal.view}
                  className={baseModal.view ? 'pt-ro-field' : undefined}
                />
              </Field>
              <div style={{ marginTop: 14, borderTop: '1px solid #eef2f7', paddingTop: 12 }}>
                <ProjectPhotos scope={PHOTO_SCOPE} apiPath={PHOTO_API} ownerKey={baseModal.baseId} readOnly={!!baseModal.view || !canEdit} />
              </div>
            </div>
            {baseModal.view ? (
              <div className="st-modal-foot st-form-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBaseModal(null)}>{t('st.viewClose')}</button>
              </div>
            ) : (
              <div className="st-modal-foot st-form-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBaseModal(null)}>{t('st.pfCancel')}</button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={saving || !baseStyleDraft.trim() || baseStyleDraft.trim() === baseModal.styleItem}
                  onClick={async () => {
                    await handleRenameSample(baseModal.baseId, baseModal.styleItem, baseStyleDraft.trim());
                    setBaseModal(null);
                  }}
                >
                  {t('st.bmRename')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
